import React, { useState, useEffect } from 'react';
import { contactsService } from '../../services/contactsService';
import DataTable from '../common/DataTable';
import { ArchiveBoxIcon, UsersIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import EditCratesModal from './EditCratesModal';
import StatCard from '../common/StatCard';

export default function CrateManagement() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [stats, setStats] = useState({
    totalCratesOut: 0,
    totalContacts: 0,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const allContacts = await contactsService.getContacts();
        const contactsWithCrates = allContacts.filter(
          contact => contact.cratesOut !== 0 && contact.contactType === 'customer'
        );
        
        // Calculate statistics
        const totalCrates = contactsWithCrates.reduce((sum, contact) => 
          sum + Math.abs(contact.cratesOut), 0);

        setContacts(contactsWithCrates);
        setStats({
          totalCratesOut: totalCrates,
          totalContacts: contactsWithCrates.length,
        });
      } catch (error) {
        console.error('Error fetching box data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCrateReturn = async (returnCount) => {
    try {
      // Ensure we have the contact ID and it's a valid number
      if (!selectedContact?.id || isNaN(returnCount)) {
        throw new Error('Invalid contact or return count');
      }

      // When customer returns crates, we subtract from their cratesOut count
      const updateData = {
        cratesOut: selectedContact.cratesOut - returnCount,
      };

      // Pass id and updateData separately
      await contactsService.updateContact(selectedContact.id, updateData);
      
      // Refresh the data
      const allContacts = await contactsService.getContacts();
      const contactsWithCrates = allContacts.filter(contact => contact.cratesOut !== 0);
      setContacts(contactsWithCrates);
      
      // Update stats
      const totalCrates = contactsWithCrates.reduce((sum, contact) => 
        sum + Math.abs(contact.cratesOut), 0);
      

      setStats({
        totalCratesOut: totalCrates,
        totalContacts: contactsWithCrates.length,
      });
    } catch (error) {
      console.error('Error updating crates:', error);
      alert('Failed to update crates count');
    }
  };

  const columns = [
    {
      header: 'Name',
      accessorKey: 'name',
      cell: (info) => (
        <div className="font-medium text-gray-900 dark:text-white">
          {info.getValue()}
        </div>
      ),
    },
    {
      header: 'Boxes Out',
      accessorKey: 'cratesOut',
      cell: (info) => (
        <span className="font-medium text-yellow-600 dark:text-yellow-400">
          {Math.abs(info.getValue())}
        </span>
      ),
    },
    {
      header: 'Phone',
      accessorKey: 'phone',
      cell: (info) => (
        <a 
          href={`tel:${info.getValue()}`}
          className="text-primary-600 hover:text-primary-800 dark:text-primary-400"
        >
          {info.getValue()}
        </a>
      ),
    },
    {
      header: 'Address',
      accessorKey: 'address',
      cell: (info) => (
        <span className="text-gray-600 dark:text-gray-400 truncate max-w-xs">
          {info.getValue()}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: (info) => (
        <button
          onClick={() => {
            setSelectedContact(info.row.original);
            setShowEditModal(true);
          }}
          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
            text-primary-700 bg-primary-100 hover:bg-primary-200 
            dark:text-primary-400 dark:bg-primary-900/50 dark:hover:bg-primary-900"
        >
          <PencilSquareIcon className="h-4 w-4 mr-1" />
          Return Boxes
        </button>
      ),
    }
  ];

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Box Management</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Track boxes given to customers and suppliers
          </p>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <StatCard
          title="Total Boxes Out"
          value={stats.totalCratesOut}
          icon={ArchiveBoxIcon}
          iconBgColor="bg-yellow-500"
        />

        <StatCard
          title="People with Boxes"
          value={stats.totalContacts}
          icon={UsersIcon}
          iconBgColor="bg-green-500"
        />
      </div>

      {/* Crates Table */}
      <div className="mt-8">
        <div className="bg-white dark:bg-[#2E3B4E] shadow rounded-lg">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              People with Boxes
            </h2>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : (
              <DataTable
                data={contacts}
                columns={columns}
                sorting={sorting}
                setSorting={setSorting}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                searchPlaceholder="Search by name, phone, or address..."
              />
            )}
          </div>
        </div>
      </div>

      {showEditModal && (
        <EditCratesModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          contact={selectedContact}
          onSubmit={handleCrateReturn}
        />
      )}
    </div>
  );
}